import React, { useState } from "react"
import { asDialogData } from "../../../core/utils/utils"
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack,
    TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import CachedIcon from "@mui/icons-material/Cached"
import {
    useGetAchievementCategoriesAdminQuery,
    useGetAllExperienceCategoriesQuery,
    useGetActiveSeasonQuery,
    useCreateAchievementCategoryMutation,
    useUpdateAchievementCategoryMutation,
    useDeleteAchievementCategoryMutation,
    useRecomputeAchievementsMutation,
    useGrantAchievementMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"
import GrantAchievementDialog from "./GrantAchievementDialog"

export default function AchievementCategoriesAdmin() {
    // Полный список по всем сезонам (пользовательский /achievements/all
    // отдаёт только активный сезон).
    const { data: achievements = [], isLoading } = useGetAchievementCategoriesAdminQuery()
    const { data: xpCategories = [] } = useGetAllExperienceCategoriesQuery()
    const { data: activeSeason } = useGetActiveSeasonQuery()
    const [createAch, createResp] = useCreateAchievementCategoryMutation()
    const [updateAch, updateResp] = useUpdateAchievementCategoryMutation()
    const [deleteAch] = useDeleteAchievementCategoryMutation()
    const [recompute, recomputeResp] = useRecomputeAchievementsMutation()

    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({ name: "", description: "", imageUrl: "", imageGreyUrl: "", xpCategoryId: "", xpThreshold: "", season: "" })
    const [grantOpen, setGrantOpen] = useState(false)
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    function openNew() {
        setEditing({})
        setForm({ name: "", description: "", imageUrl: "", imageGreyUrl: "", xpCategoryId: "", xpThreshold: "", season: activeSeason?.number ?? "" })
    }
    function openEdit(row) {
        setEditing(row)
        setForm({
            name: row.name, description: row.description || "", imageUrl: row.imageUrl || "",
            imageGreyUrl: row.imageGreyUrl || "",
            xpCategoryId: row.xpCategoryId || "",
            xpThreshold: row.xpThreshold != null ? String(row.xpThreshold) : "",
            season: row.season ?? "",
        })
    }
    function close() { setEditing(null) }

    async function save() {
        const body = {
            name: form.name,
            description: form.description,
            imageUrl: form.imageUrl,
            // Пустая строка при update очищает поле → возврат к CSS-grayscale.
            imageGreyUrl: form.imageGreyUrl,
            xpCategoryId: form.xpCategoryId || null,
            xpThreshold: form.xpThreshold ? parseInt(form.xpThreshold, 10) : null,
        }
        // Сезон задаётся только при создании (менять сезон записи нельзя);
        // пустое поле → активный сезон (решает бэкенд).
        if (!editing.id && form.season !== "") {
            body.season = parseInt(form.season, 10)
        }
        const resp = editing.id
            ? await updateAch({ id: editing.id, ...body })
            : await createAch(body)
        setActionResp(asDialogData(resp)); setRespOpen(true); close()
    }
    async function remove(row) {
        if (!window.confirm(`Delete «${row.name}»?`)) return
        const resp = await deleteAch(row.id)
        setActionResp(asDialogData(resp)); setRespOpen(true)
    }
    async function doRecompute() {
        if (!window.confirm("Recompute achievements for all active employees? This may take a while.")) return
        const resp = await recompute()
        setActionResp(asDialogData(resp)); setRespOpen(true)
    }

    const columns = [
        { field: "season", headerName: "Season", width: 90, renderCell: (p) => p.value ?? "—" },
        { field: "name", headerName: "Name", width: 180 },
        { field: "description", headerName: "Description", flex: 1 },
        { field: "xpCategoryId", headerName: "XP category", width: 130, renderCell: (p) => {
            const c = xpCategories.find(x => x.id === p.value)
            return c ? c.name : "—"
        }},
        { field: "xpThreshold", headerName: "Threshold", width: 110, renderCell: (p) => p.value ?? "—" },
        { field: "actions", headerName: "", width: 100, renderCell: (p) =>
            <Stack direction="row">
                <IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(p.row)}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
        },
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Achievements</Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<CachedIcon />} onClick={doRecompute} disabled={recomputeResp.isLoading}>
                        Recompute
                    </Button>
                    <Button variant="outlined" onClick={() => setGrantOpen(true)}>Grant manually</Button>
                    <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openNew}>
                        New achievement
                    </Button>
                </Stack>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
                If «XP category» + «XP threshold» are set, the achievement is granted automatically on accumulation. Otherwise — manual only.
                The «Recompute» button runs a backfill over all active employees — use it after adding a new achievement.
            </Alert>
            <Paper sx={{ height: 560 }}>
                <DataGrid
                    rows={achievements}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!editing} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.id ? "Edit" : "New achievement"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                        <TextField label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} multiline minRows={2} />
                        <TextField label="Image URL" value={form.imageUrl} onChange={e => setForm({ ...form, imageUrl: e.target.value })} />
                        <TextField
                            label="Image URL (grey, not earned)"
                            helperText="Серый вариант для незаработанного щита (напр. /achievements_season2/grey/100.png). Пусто — старое поведение: CSS grayscale поверх основной картинки"
                            value={form.imageGreyUrl}
                            onChange={e => setForm({ ...form, imageGreyUrl: e.target.value })}
                        />
                        <TextField
                            select
                            label="XP category (for auto-grant)"
                            helperText="Empty + threshold set = total-XP shield (100 XP … XPmaxxer)"
                            value={form.xpCategoryId}
                            onChange={e => setForm({ ...form, xpCategoryId: e.target.value })}
                        >
                            <MenuItem value="">— total XP (shield) / manual —</MenuItem>
                            {xpCategories.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}{c.season ? ` (сезон ${c.season})` : ""}</MenuItem>
                            ))}
                        </TextField>
                        {/* Порог без категории — валидный кейс (щиты по totalXp),
                            поэтому поле не блокируется отсутствием категории. */}
                        <TextField
                            label="XP threshold (for auto-grant)"
                            type="number"
                            value={form.xpThreshold}
                            onChange={e => setForm({ ...form, xpThreshold: e.target.value })}
                        />
                        <TextField
                            label="Season"
                            type="number"
                            helperText={editing?.id ? "Сезон записи менять нельзя" : "Пусто — активный сезон; будущий номер — подготовка контента заранее"}
                            value={form.season}
                            onChange={e => setForm({ ...form, season: e.target.value })}
                            disabled={!!editing?.id}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={save} disabled={createResp.isLoading || updateResp.isLoading}>Save</Button>
                </DialogActions>
            </Dialog>

            <GrantAchievementDialog open={grantOpen} onClose={() => setGrantOpen(false)} />

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Achievement"
                information={actionResp.data && actionResp.data.scannedEmployees !== undefined
                    ? `Scanned ${actionResp.data.scannedEmployees} employees, granted ${actionResp.data.grantedAchievements} achievements`
                    : "Saved"}
            />
        </Box>
    )
}
